import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { rupiah, pct } from "@/lib/format";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { toast } from "sonner";
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from "recharts";
import { Store, Users, Building2, Wallet, Save, Calculator } from "lucide-react";

export default function RevenueModel() {
  const [settings, setSettings] = useState(null);
  const [sim, setSim] = useState(1000000);
  const [merchantRate, setMerchantRate] = useState(0.1);
  const [talentShare, setTalentShare] = useState(0.8);
  const [fee, setFee] = useState(3000);

  useEffect(() => {
    api.get("/settings").then((res) => {
      setSettings(res.data);
      setMerchantRate(res.data.merchant_commission_rate);
      setTalentShare(res.data.talent_share_pct);
      setFee(res.data.tarapay_flat_fee ?? res.data.easylink_flat_fee ?? 3000);
    }).catch(() => {});
  }, []);

  const pool = Math.round(sim * merchantRate);
  const talentCommission = Math.round(pool * talentShare);
  const agencyRevenue = pool - talentCommission;
  const talentNet = talentCommission - fee;

  const pieData = [
    { name: "Revenue Agency", value: agencyRevenue, color: "#10B981" },
    { name: "Komisi Talent", value: talentCommission, color: "#F59E0B" },
    { name: "Sisa GMV (Merchant)", value: sim - pool, color: "#1F2937" },
  ];

  const save = async () => {
    try {
      const { data } = await api.put("/settings", {
        merchant_commission_rate: merchantRate,
        talent_share_pct: talentShare,
        tarapay_flat_fee: Number(fee),
        easylink_flat_fee: Number(fee),
      });
      setSettings(data);
      toast.success("Model revenue diperbarui");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const flow = [
    { icon: Store, label: "Merchant / Platform", desc: `Membayar komisi ${pct(merchantRate)} dari GMV`, color: "#06B6D4", value: pool },
    { icon: Building2, label: "Komisia (Agency)", desc: `Menahan margin ${pct(1 - talentShare)} (platform + management fee)`, color: "#10B981", value: agencyRevenue },
    { icon: Users, label: "Talent Afiliasi", desc: `Menerima ${pct(talentShare)} dari commission pool`, color: "#F59E0B", value: talentCommission },
    { icon: Wallet, label: "Disbursement", desc: `Dikurangi biaya transfer ${rupiah(fee)}`, color: "#6366F1", value: talentNet },
  ];

  if (!settings) return <div className="text-muted-foreground">Memuat...</div>;

  return (
    <div className="space-y-8" data-testid="revenue-model-container">
      <div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight">Model Bisnis & Revenue</h1>
        <p className="text-muted-foreground mt-1">Bagaimana Komisia menghasilkan pendapatan — transparan & auditable</p>
      </div>

      {/* Flow explainer */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {flow.map((f, i) => (
          <motion.div
            key={i}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.08 * i }}
            className="bg-card border border-border rounded-xl p-5 relative overflow-hidden"
          >
            <div className="absolute top-3 right-3 text-xs font-bold text-muted-foreground">{i + 1}</div>
            <div className="h-11 w-11 rounded-lg grid place-items-center mb-3" style={{ background: `${f.color}22` }}>
              <f.icon className="h-6 w-6" style={{ color: f.color }} />
            </div>
            <p className="font-semibold">{f.label}</p>
            <p className="text-xs text-muted-foreground mt-1 min-h-[32px]">{f.desc}</p>
            <p className="font-display text-xl font-bold mt-2 font-mono" style={{ color: f.color }}>{rupiah(f.value)}</p>
          </motion.div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Simulator */}
        <div className="bg-card border border-border rounded-xl p-6 space-y-6">
          <div className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            <h3 className="font-semibold">Simulator Margin & Parameter</h3>
          </div>

          <div>
            <Label>Simulasi GMV per Order</Label>
            <Input data-testid="sim-gmv-input" type="number" value={sim} onChange={(e) => setSim(Number(e.target.value) || 0)} className="mt-1.5 font-mono" />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <Label>Komisi Merchant (% GMV)</Label>
              <span className="font-mono text-primary text-sm">{pct(merchantRate)}</span>
            </div>
            <Slider data-testid="merchant-rate-slider" value={[merchantRate * 100]} onValueChange={(v) => setMerchantRate(v[0] / 100)} min={1} max={30} step={1} />
          </div>

          <div>
            <div className="flex justify-between mb-2">
              <Label>Bagi Hasil Talent (% pool)</Label>
              <span className="font-mono text-amber-400 text-sm">{pct(talentShare)}</span>
            </div>
            <Slider data-testid="talent-share-slider" value={[talentShare * 100]} onValueChange={(v) => setTalentShare(v[0] / 100)} min={50} max={95} step={1} />
          </div>

          <div>
            <Label>Biaya Disbursement (Rp)</Label>
            <Input type="number" value={fee} onChange={(e) => setFee(e.target.value)} className="mt-1.5 font-mono" />
          </div>

          <div className="bg-background rounded-lg p-4 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-muted-foreground">Commission Pool</span><span className="font-mono">{rupiah(pool)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Revenue Agency (margin {pct(1 - talentShare)})</span><span className="font-mono text-emerald-400">{rupiah(agencyRevenue)}</span></div>
            <div className="flex justify-between"><span className="text-muted-foreground">Komisi Talent</span><span className="font-mono text-amber-400">{rupiah(talentCommission)}</span></div>
            <div className="flex justify-between border-t border-border pt-2"><span className="text-muted-foreground">Diterima Talent (net)</span><span className="font-mono text-primary">{rupiah(talentNet)}</span></div>
          </div>

          <Button data-testid="save-revenue-settings" onClick={save} className="w-full bg-primary hover:bg-primary/90 text-white">
            <Save className="h-4 w-4 mr-2" /> Simpan sebagai Default Sistem
          </Button>
        </div>

        {/* Pie breakdown */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h3 className="font-semibold mb-4">Distribusi 1 Order (GMV {rupiah(sim)})</h3>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie data={pieData} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={70} outerRadius={110} paddingAngle={3}>
                {pieData.map((d, i) => <Cell key={i} fill={d.color} stroke="#0B0F17" strokeWidth={2} />)}
              </Pie>
              <Tooltip contentStyle={{ background: "#111827", border: "1px solid #1F2937", borderRadius: 12 }} formatter={(v) => rupiah(v)} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
            </PieChart>
          </ResponsiveContainer>
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-lg p-4 mt-2">
            <p className="text-sm font-semibold text-emerald-400">Ringkasan Model Bisnis</p>
            <p className="text-xs text-muted-foreground mt-1 leading-relaxed">
              Agency mendapat penghasilan dari <span className="text-emerald-400 font-medium">management fee & platform cut</span> sebesar {pct(1 - talentShare)} dari commission pool tiap penjualan afiliasi.
              Talent menerima {pct(talentShare)} yang di-disburse otomatis. Model ini scalable mengikuti pertumbuhan GMV.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
